import { Box, Flex, Text } from "rebass/styled-components";
import styled from "styled-components";
import { FaArrowRight, FaPlay } from "react-icons/fa";
import { useRecoilValue } from "recoil";
import { useEffect } from "react";

import { NotificationBannerViewModel } from "./ViewModel";
import { IClip } from "MVVM/@types";
import { TUserRole } from "../../@types";
import { useAnalyticsContext } from "MVVM/Hooks/useAnalyticsContext";
import "./index.css";
import {
  ActiveOverlayPopUpState,
  PlatformState,
  ShouldDisplayOverlayState,
} from "MVVM/State/iframe";
import { isPartner, Partner } from "MVVM/Views/EmbeddablePlaylist/Utilities";

export const NotificationBanner = ({
  clip,
  userRole = "creator",
}: {
  clip: IClip;
  userRole?: TUserRole;
}) => {
  const { track } = useAnalyticsContext();
  const { notificationData, loading, showProfileCTA } =
    NotificationBannerViewModel(clip, userRole);

  const activeOverlayPopUp = useRecoilValue(ActiveOverlayPopUpState);
  const shouldDisplayOverlay = useRecoilValue(ShouldDisplayOverlayState);
  const platform = useRecoilValue(PlatformState);

  const isHLTV = isPartner(platform, Partner.HLTV);

  useEffect(() => {
    if (loading || activeOverlayPopUp || !shouldDisplayOverlay) return;
    track("Component Viewed", {
      location: "embeddable_player_overlay",
      locationIdentifier: "notification_banner",
      promotionName: notificationData.promotionName,
    });
  }, [
    activeOverlayPopUp,
    notificationData.promotionName,
    loading,
    track,
    shouldDisplayOverlay,
  ]);

  const handleClick = () => {
    try {
      track("Button - Click", {
        location: "embeddable_player_overlay",
        locationIdentifier: "notification_banner",
        promotionName: notificationData.promotionName,
      });
      const ajs_aid = window.rudderanalytics.getAnonymousId();
      const separator = notificationData.buttonLink.includes("?") ? "&" : "?";
      window.open(
        `${notificationData.buttonLink}${separator}ajs_aid=${ajs_aid}`,
        "_blank",
      );
    } catch (e) {
      console.log(e);
    }
  };

  if (activeOverlayPopUp || loading) return null;

  return (
    <Container
      $hideImageOnSmallScreen={
        notificationData.thumbnail === "static/matchhistory.png"
      }
      onClick={handleClick}
      className={`notification-banner ${
        notificationData.thumbnail === "static/matchhistory.png"
          ? "hide-image-on-small"
          : ""
      }`}
    >
      {!!notificationData.thumbnail &&
        (showProfileCTA && isHLTV ? (
          <ProPlayerPortraitContainer onClick={handleClick}>
            <ProPlayerPortrait src={notificationData.thumbnail} />
            <ProPlayerPortraitBackground />
          </ProPlayerPortraitContainer>
        ) : (
          <ThumbnailContainer onClick={handleClick}>
            <Thumbnail
              src={`${process.env.REACT_APP_S3_CLIP_BUCKET}/${notificationData.thumbnail}`}
              $hideOnSmallScreen={
                notificationData.thumbnail === "static/matchhistory.png"
              }
              className={`notification-banner-thumbnail ${
                notificationData.thumbnail === "static/matchhistory.png"
                  ? "hide-on-small"
                  : ""
              }`}
            />
            {notificationData.showPlayButton && (
              <StyledPlayButton className="notification-banner-play-button" />
            )}
            <ThumbnailBackground
              $hideOnSmallScreen={
                notificationData.thumbnail === "static/matchhistory.png"
              }
              className={`notification-banner-thumbnail-background ${
                notificationData.thumbnail === "static/matchhistory.png"
                  ? "hide-on-small"
                  : ""
              }`}
            />
          </ThumbnailContainer>
        ))}
      <BannerText className="banner-text">
        <span className="small-screen-text">
          {notificationData.smallScreenText}
        </span>
        <span className="large-screen-text">
          {notificationData.largeScreenText}
        </span>
      </BannerText>
      <GoArrowContainer className="go-arrow-container">
        <GoArrow className="go-arrow" />
      </GoArrowContainer>
    </Container>
  );
};

const ProPlayerPortraitContainer = styled(Flex)`
  cursor: pointer;
  left: 8px;
  pointer-events: auto;
  position: absolute;
  top: 48%;
  transform: translateY(-50%);
`;

const ProPlayerPortrait = styled.img`
  aspect-ratio: 1 / 1;
  background-color: ${({ theme }) => theme.colors.ned};
  border: ${({ theme }) => `1px solid ${theme.colors.envy}`};
  border-radius: 4px;
  box-shadow: ${({ theme }) =>
    `0 0 #0000, 0 0 #0000, ${theme.colors.envyAlpha30} 0px 0px 16px 0px`};
  flex-shrink: 0;
  height: 42px;
  object-fit: cover;
  object-position: center 10%;
  position: relative;
  width: 42px;
  z-index: 10;

  @media (max-width: 504px) {
    height: 32px;
    width: 32px;
  }
`;

const ProPlayerPortraitBackground = styled(Box)`
  aspect-ratio: 1 / 1;
  background-color: ${({ theme }) => theme.colors.navy200};
  border: ${({ theme }) => `1px solid ${theme.colors.envy}`};
  border-radius: 4px;
  box-shadow: ${({ theme }) =>
    `0 0 #0000, 0 0 #0000, ${theme.colors.envyAlpha30} 0px 0px 16px 0px`};
  flex-shrink: 0;
  height: 42px;
  position: absolute;
  top: 0;
  transform: rotate(-6deg);
  width: 42px;
  z-index: -50;

  @media (max-width: 504px) {
    height: 32px;
    width: 32px;
  }
`;

const ThumbnailContainer = styled(Flex)`
  cursor: pointer;
  left: -25px;
  pointer-events: auto;
  position: absolute;
  top: 48%;
  transform: translateY(-50%);
`;

const StyledPlayButton = styled(FaPlay)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 6px;
  left: 50%;
  position: absolute;
  top: 50%;
  transform: translate(-50%, -50%);
`;

const Thumbnail = styled.img<{
  $hideOnSmallScreen?: boolean;
}>`
  aspect-ratio: 16 / 9;
  width: 50px;
  height: auto;
  border-radius: 4px;
  border: ${({ theme }) => `1px solid ${theme.colors.envy}`};
  box-shadow: ${({ theme }) =>
    `0 0 #0000, 0 0 #0000, ${theme.colors.envyAlpha30} 0px 0px 16px 0px`};
  flex-shrink: 0;
  object-fit: cover;
  object-position: center;
  position: relative;
`;

const ThumbnailBackground = styled(Box)<{
  $hideOnSmallScreen?: boolean;
}>`
  aspect-ratio: 16 / 9;
  background-color: ${({ theme }) => theme.colors.ned};
  border: ${({ theme }) => `1px solid ${theme.colors.envy};`}
  border-radius: 4px;
  box-shadow: ${({ theme }) =>
    `0 0 #0000, 0 0 #0000, ${theme.colors.envyAlpha30} 0px 0px 16px 0px`};
  flex-shrink: 0;
  height: auto;
  position: absolute;
  top: 0;
  transform: rotate(-6deg);
  width: 50px;
  z-index: -50;
`;

const BannerText = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-family: Barlow;
  font-size: 8px;
  font-weight: 500;
  letter-spacing: 0%;
  line-height: 10px;
  vertical-align: middle;
  margin-left: 4px;

  @media (max-width: 504px) {
    margin-left: 12px;
  }

  .small-screen-text {
    display: inline;
  }

  .large-screen-text {
    display: none;
  }
`;

const GoArrow = styled(FaArrowRight)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 8px;
`;

const GoArrowContainer = styled(Flex)`
  align-items: center;
  background-color: ${({ theme }) => theme.colors.envy};
  border-radius: 50%;
  cursor: pointer;
  height: 12px;
  justify-content: center;
  pointer-events: auto;
  width: 12px;
  flex-shrink: 0;
  &:hover {
    background-color: ${({ theme }) => theme.colors.envyAlpha80};
  }
`;

const Container = styled(Flex)<{
  $hideImageOnSmallScreen?: boolean;
}>`
  align-items: center;
  background-color: #000000bf;
  border-radius: 10px;
  gap: 4px;
  justify-content: space-between;
  padding: 4px;
  padding-left: 35px;
  position: absolute;
  right: 5px;
  top: 20px;
  cursor: pointer;
  pointer-events: auto;
`;
